import { useState } from "react";
import { useFormContext, useController } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import {
  FieldBaseProps,
  useFieldProps,
} from "@shared/features/form/useFieldProps";
import { Button } from "@shared/ui/Button";
import { Type } from "@shared/ui/Type";
import { FileUploader } from "@shared/ui/FileUploader";

export type GenericUploadInputProps = FieldBaseProps & {
  name: string;
  hash: string;
  maxFileSize: number;
  allowedFileTypes?: Record<string, boolean>;
  acceptedFileTypes?: string;
  resize?: (file: File) => Promise<File>;
  preview?: (value: any) => React.ReactNode;
  transform?: (file: File, base64: string) => any;
  renderDetails?: (fileData: any) => React.ReactNode;
  changeButtonLabel?: string;
};

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });
}

export function GenericUploadInput({
  name,
  required,
  error,
  isSubmitting,
  className,
  hash,
  maxFileSize,
  allowedFileTypes,
  acceptedFileTypes,
  resize,
  transform,
  preview,
  renderDetails,
  changeButtonLabel = "Change file",
}: GenericUploadInputProps) {
  const { control } = useFormContext();

  const {
    field: { value = null, onChange },
  } = useController({
    name,
    control,
    rules: {
      required: required ? "This field is required" : false,
    },
  });

  const [errorText, setErrorText] = useState<string | null>(null);
  const [isChanged, setIsChanged] = useState<boolean>(false);

  const fieldProps = useFieldProps({
    className: twMerge(
      "border-none p-0 flex flex-col gap-2",
      isChanged && "shadow-none",
      className
    ),
    error,
    required,
    isSubmitting,
  });

  const handleFileUpload = async (
    file: File,
    onProgress: (progress: number) => void
  ) => {
    setErrorText(null);

    try {
      if (allowedFileTypes && !allowedFileTypes[file.type]) {
        setErrorText(`File type ${file.type} is not supported.`);
        return;
      }

      const resized = resize ? await resize(file) : file;
      if (resized.size > maxFileSize) {
        setErrorText("File too large.");
        return;
      }

      const base64 = await fileToBase64(resized);

      const finalValue = transform
        ? await transform(resized, base64)
        : {
            file: base64,
            name: resized.name,
            size: resized.size,
            type: resized.type,
            original: resized,
          };

      onChange(finalValue);

      setIsChanged(true);
    } catch (e: any) {
      setErrorText(e.message || "Upload failed");
    }
  };

  return (
    <div {...fieldProps} className="shadow-none">
      {value?.file && (
        <div className="mt-2 flex flex-col items-start gap-2">
          {preview?.(value)}
          {renderDetails?.(value)}

          <Button
            variant="secondary"
            data-testid="generic-upload-input-change-button"
            onClick={() =>
              document.getElementById(`file-upload${hash}`)?.click()
            }
          >
            {changeButtonLabel}
          </Button>

          {isChanged && (
            <Type variant="h6" className="text-success">
              Click "Save" to apply changes
            </Type>
          )}
        </div>
      )}

      {errorText && (
        <Type variant="caption" className="text-error mt-2">
          {errorText}
        </Type>
      )}

      <FileUploader
        hash={hash}
        uploadFileHandler={handleFileUpload}
        className={value?.file ? "hidden" : ""}
        allowedFileTypes={allowedFileTypes}
        acceptedFileTypes={acceptedFileTypes}
      />
    </div>
  );
}

export default GenericUploadInput;
